import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';
import { PatientFormData } from '../types/forms';

let authToken: string | null = null;

export const setAuthToken = (token?: string | null) => {
  authToken = token ?? null;
};

export const getAuthToken = () => authToken;

export const clearAuthToken = () => {
  authToken = null;
};

export interface QueryEnvelope<T> {
  value: T;
  fallback: boolean;
  message?: string;
}

const wrapValue = <T>(value: T, fallback = false, message?: string): QueryEnvelope<T> => ({
  value,
  fallback,
  message,
});

const toHookResult = <T>(
  query: UseQueryResult<QueryEnvelope<T>>,
  fallbackValue: T,
) => ({
  ...query,
  data: query.data?.value ?? fallbackValue,
  isFallback: query.data?.fallback ?? false,
  fallbackMessage: query.data?.message,
});

export class ApiError extends Error {
  constructor(message: string, public status?: number) {
    super(message);
    this.name = 'ApiError';
  }
}

const request = async <T>(
  input: RequestInfo,
  schema: z.ZodSchema<T>,
  init?: RequestInit,
): Promise<T> => {
  const headers = new Headers({
    Accept: 'application/json',
    'Content-Type': 'application/json',
  });

  if (authToken) {
    headers.set('Authorization', `Bearer ${authToken}`);
  }

  if (init?.headers) {
    const incoming = new Headers(init.headers);
    incoming.forEach((value, key) => {
      headers.set(key, value);
    });
  }

  const response = await fetch(input, {
    ...init,
    headers,
  });

  if (!response.ok) {
    throw new ApiError(`Request failed (${response.status})`, response.status);
  }

  const payload = await response.json();
  return schema.parse(payload);
};

// Schemas & types
export const patientSchema = z.object({
  id: z.string().or(z.number()),
  name: z.string(),
  age: z.number().nonnegative(),
  species: z.string(),
  owner: z.string().optional(),
  lastVisit: z.string().optional(),
  doctor: z.string().optional(),
});
export type Patient = z.infer<typeof patientSchema>;
const patientsSchema = z.array(patientSchema);

export const appointmentSchema = z.object({
  id: z.string().or(z.number()),
  date: z.string(),
  petName: z.string(),
  vetName: z.string(),
  type: z.string().optional(),
  room: z.string().optional(),
  owner: z.string().optional(),
  status: z.string().optional(),
});
export type Appointment = z.infer<typeof appointmentSchema>;
const appointmentsSchema = z.array(appointmentSchema);

const ROLE_LITERALS = ['medical_director', 'surgery', 'internal_med', 'customer_service', 'billing'] as const;
export type RoleSlug = (typeof ROLE_LITERALS)[number];

const isRoleSlug = (value: string): value is RoleSlug =>
  (ROLE_LITERALS as readonly string[]).includes(value);

export const roleSchema = z
  .string()
  .refine(isRoleSlug, { message: 'Invalid role assignment' }) as z.ZodType<RoleSlug>;

const carePlanRound = z.object({
  patient: z.string(),
  diagnosis: z.string(),
  priority: z.string(),
  attending: z.string(),
  nextStep: z.string(),
});
const careReminder = z.object({
  title: z.string(),
  due: z.string(),
  channel: z.string(),
});
const surgeryEntry = z.object({
  time: z.string(),
  case: z.string(),
  room: z.string(),
  lead: z.string(),
});
const inventoryAlert = z.object({
  item: z.string(),
  status: z.string(),
  action: z.string(),
});

export interface CarePlanPayload {
  rounds: z.infer<typeof carePlanRound>[];
  reminders: z.infer<typeof careReminder>[];
  surgeryBoard: z.infer<typeof surgeryEntry>[];
  inventoryAlerts: z.infer<typeof inventoryAlert>[];
}

const carePlanSchema: z.ZodSchema<CarePlanPayload> = z.object({
  rounds: z.array(carePlanRound),
  reminders: z.array(careReminder),
  surgeryBoard: z.array(surgeryEntry),
  inventoryAlerts: z.array(inventoryAlert),
});

const invoiceSchema = z.object({
  id: z.string(),
  client: z.string(),
  patient: z.string(),
  balance: z.string(),
  status: z.string(),
  due: z.string(),
});
const insuranceSchema = z.object({
  carrier: z.string(),
  claim: z.string(),
  amount: z.string(),
  stage: z.string(),
});
const spendSchema = z.object({
  category: z.string(),
  amount: z.string(),
  trend: z.string(),
});

export interface BillingWorkspacePayload {
  invoices: z.infer<typeof invoiceSchema>[];
  insuranceQueue: z.infer<typeof insuranceSchema>[];
  inventorySpend: z.infer<typeof spendSchema>[];
}

const billingSchema: z.ZodSchema<BillingWorkspacePayload> = z.object({
  invoices: z.array(invoiceSchema),
  insuranceQueue: z.array(insuranceSchema),
  inventorySpend: z.array(spendSchema),
});

export const patientNoteSchema = z.object({
  id: z.string(),
  patientId: z.string(),
  author: z.string(),
  role: z.string(),
  category: z.string(),
  note: z.string(),
  createdAt: z.string(),
});
export type PatientNote = z.infer<typeof patientNoteSchema>;
const patientNotesSchema = z.array(patientNoteSchema);

const prescriptionSchema = z.object({
  id: z.string(),
  patientId: z.string(),
  medication: z.string(),
  dosage: z.string().optional(),
  frequency: z.string().optional(),
  duration: z.string().optional(),
  instructions: z.string().optional(),
  status: z.string().optional(),
  createdAt: z.string(),
});
export type Prescription = z.infer<typeof prescriptionSchema>;
const prescriptionsSchema = z.array(prescriptionSchema);

const labResultSchema = z.object({
  id: z.string(),
  patientId: z.string(),
  testName: z.string(),
  status: z.string().optional(),
  findings: z.string().optional(),
  performedAt: z.string().nullable().optional(),
  attachmentId: z.string().nullable().optional(),
  createdAt: z.string(),
});
export type LabResult = z.infer<typeof labResultSchema>;
const labResultsSchema = z.array(labResultSchema);

const attachmentSchema = z.object({
  id: z.string(),
  patientId: z.string(),
  filename: z.string(),
  contentType: z.string().optional(),
  contentBase64: z.string().optional(),
  sizeBytes: z.number().optional(),
  source: z.string().optional(),
  createdAt: z.string(),
});
export type PatientAttachment = z.infer<typeof attachmentSchema>;
const attachmentsSchema = z.array(attachmentSchema);

const appointmentActionSchema = z.object({
  id: z.string(),
  status: z.string().optional(),
  previousStatus: z.string().optional(),
  updatedAt: z.string().optional(),
});
const reminderResponseSchema = z.object({
  id: z.string(),
  channel: z.string(),
  sentAt: z.string(),
});
const billingActionSchema = z.object({
  id: z.string(),
  status: z.string().optional(),
  balance: z.string().optional(),
  updatedAt: z.string().optional(),
});

const opsMetricSchema = z.object({
  id: z.string(),
  label: z.string(),
  value: z.number(),
  trend: z.string(),
});

const opsStatusSchema = z.object({
  status: z.string(),
  count: z.number(),
});

const opsSummarySchema = z.object({
  metrics: z.array(opsMetricSchema),
  appointmentStatus: z.array(opsStatusSchema),
  revenue: z.object({
    pending: z.number(),
    collected: z.number(),
  }),
});

export type OpsSummary = z.infer<typeof opsSummarySchema>;

export interface PrefetchBundle {
  patients: Patient[];
  appointments: Appointment[];
  carePlans: CarePlanPayload;
  billing: BillingWorkspacePayload;
  ops: OpsSummary;
}

const authUserSchema = z.object({
  email: z.string().email(),
  name: z.string(),
  role: roleSchema,
});
export type AuthenticatedUser = z.infer<typeof authUserSchema>;

const loginResponseSchema = z.object({
  token: z.string(),
  user: authUserSchema,
});
export type LoginResponse = z.infer<typeof loginResponseSchema>;

export const auditLogEntrySchema = z.object({
  id: z.string(),
  actor: z.string(),
  role: z.string(),
  action: z.string(),
  resource: z.string(),
  timestamp: z.string(),
  detail: z.string().optional(),
});
export type AuditLogEntry = z.infer<typeof auditLogEntrySchema>;
const auditLogSchema = z.array(auditLogEntrySchema);

// Fallback data
const fallbackPatients: Patient[] = [
  {
    id: 'luna',
    name: 'Luna',
    age: 4,
    species: 'canine',
    owner: 'Mia Lin',
    doctor: 'Dr. Avery',
    lastVisit: 'Mar 3, 2025',
  },
  {
    id: 'max',
    name: 'Max',
    age: 7,
    species: 'canine',
    owner: 'Leo Chang',
    doctor: 'Dr. Patel',
    lastVisit: 'Feb 28, 2025',
  },
  {
    id: 'bailey',
    name: 'Bailey',
    age: 6,
    species: 'feline',
    owner: 'Owen Wu',
    doctor: 'Dr. Ramos',
    lastVisit: 'Mar 1, 2025',
  },
];

const fallbackAppointments: Appointment[] = [
  {
    id: 'appt-1',
    date: new Date().toISOString(),
    petName: 'Luna',
    owner: 'Mia Lin',
    vetName: 'Dr. Avery',
    type: 'Triage',
    room: 'Treatment A',
    status: 'Check-in',
  },
  {
    id: 'appt-2',
    date: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
    petName: 'Max',
    owner: 'Leo Chang',
    vetName: 'Dr. Patel',
    type: 'Surgery prep',
    room: 'OR 2',
    status: 'Pre-op',
  },
  {
    id: 'appt-3',
    date: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
    petName: 'Bailey',
    owner: 'Owen Wu',
    vetName: 'Dr. Ramos',
    type: 'Follow up',
    room: 'Exam 4',
    status: 'Follow-up',
  },
];

const fallbackCarePlans: CarePlanPayload = {
  rounds: [
    {
      patient: 'Luna',
      diagnosis: 'Immune-mediated hemolytic anemia',
      priority: 'Critical',
      attending: 'Dr. Patel',
      nextStep: 'Transfusion review',
    },
    {
      patient: 'Kobe',
      diagnosis: 'TPLO post-op day 1',
      priority: 'High',
      attending: 'Dr. Ramos',
      nextStep: 'Pain reassessment 18:00',
    },
    {
      patient: 'Miso',
      diagnosis: 'Pancreatitis',
      priority: 'Moderate',
      attending: 'Dr. Alvarez',
      nextStep: 'Re-evaluate hydration',
    },
  ],
  reminders: [
    { title: 'Bailey - Sutures removal', due: 'Tomorrow 09:00', channel: 'SMS + Email' },
    { title: 'Ori - Cardiology recheck', due: 'Fri 14:30', channel: 'Owner portal' },
    { title: 'Nala - Vaccine booster', due: 'Next week', channel: 'Push notification' },
  ],
  surgeryBoard: [
    { time: '08:00', case: 'Luna - splenectomy', room: 'OR 2', lead: 'Dr. Patel' },
    { time: '10:30', case: 'Atlas - orthopedics consult', room: 'Ortho', lead: 'Dr. Ramos' },
    { time: '13:15', case: 'Nova - ophthalmology', room: 'Procedure', lead: 'Dr. Khan' },
  ],
  inventoryAlerts: [
    { item: 'Isoflurane', status: '18 hrs on hand', action: 'Auto-reorder triggered' },
    { item: 'Fentanyl patches', status: '8 remaining', action: 'Pharmacy notified' },
    { item: 'Sutures 3-0', status: 'Reorder placed', action: 'ETA 2 days' },
  ],
};

const fallbackBillingWorkspace: BillingWorkspacePayload = {
  invoices: [
    { id: 'INV-9201', client: 'Mia Lin', patient: 'Luna', balance: '$1,240', status: 'Pending', due: 'Today' },
    { id: 'INV-9195', client: 'Leo Chang', patient: 'Max', balance: '$780', status: 'Installments', due: 'Next week' },
    { id: 'INV-9192', client: 'Ava Huang', patient: 'Nova', balance: '$320', status: 'Paid', due: 'Cleared' },
  ],
  insuranceQueue: [
    { carrier: 'Trupanion', claim: '#T-4211', amount: '$2,310', stage: 'Review' },
    { carrier: 'Nationwide', claim: '#N-1880', amount: '$890', stage: 'Submitted' },
  ],
  inventorySpend: [
    { category: 'Pharmacy', amount: '$4,520', trend: '+8% vs last week' },
    { category: 'Surgery supplies', amount: '$3,180', trend: '-2%' },
    { category: 'Lab services', amount: '$1,450', trend: '+4%' },
  ],
};

const fallbackPatientNotes: Record<string, PatientNote[]> = {
  luna: [
    {
      id: 'note-luna-1',
      patientId: 'luna',
      author: 'Dr. Avery',
      role: 'Medical Director',
      category: 'Rounds',
      note: 'HCT stabilized after transfusion. Continue immunosuppressants and taper fluids over 8 hrs.',
      createdAt: '2025-03-05T09:00:00Z',
    },
    {
      id: 'note-luna-2',
      patientId: 'luna',
      author: 'RN Kai',
      role: 'ICU Nurse',
      category: 'Nursing',
      note: 'Patient eating 50% of breakfast, resting comfortably. Next vitals at 11:00.',
      createdAt: '2025-03-05T08:15:00Z',
    },
  ],
  max: [
    {
      id: 'note-max-1',
      patientId: 'max',
      author: 'Dr. Ramos',
      role: 'Surgeon',
      category: 'Surgery',
      note: 'Reviewed TPLO post-op films with owner. Reinforce restricted activity for 6 weeks.',
      createdAt: '2025-03-04T16:45:00Z',
    },
  ],
  default: [
    {
      id: 'note-default-1',
      patientId: 'default',
      author: 'System',
      role: 'Automation',
      category: 'General',
      note: 'New patient onboarded. Awaiting first triage exam.',
      createdAt: new Date().toISOString(),
    },
  ],
};

const getFallbackPatientNotes = (patientId: string | number) => {
  const key = patientId.toString().toLowerCase();
  return (
    fallbackPatientNotes[patientId.toString()] ??
    fallbackPatientNotes[key] ??
    fallbackPatientNotes.default
  );
};

const fallbackPrescriptions: Record<string, Prescription[]> = {
  luna: [
    {
      id: 'rx-luna-1',
      patientId: 'luna',
      medication: 'Prednisone 5mg',
      dosage: '5mg',
      frequency: 'BID',
      duration: '14 days',
      instructions: 'Give with food, taper per plan',
      status: 'active',
      createdAt: '2025-03-05T08:00:00Z',
    },
  ],
  default: [],
};

const fallbackLabResults: Record<string, LabResult[]> = {
  luna: [
    {
      id: 'lab-luna-1',
      patientId: 'luna',
      testName: 'CBC',
      status: 'Completed',
      findings: 'PCV trending down, retics stable.',
      performedAt: '2025-03-05T06:45:00Z',
      attachmentId: null,
      createdAt: '2025-03-05T07:00:00Z',
    },
  ],
  default: [],
};

const fallbackAttachments: Record<string, PatientAttachment[]> = {
  luna: [
    {
      id: 'file-luna-1',
      patientId: 'luna',
      filename: 'cbc_results.pdf',
      contentType: 'application/pdf',
      contentBase64: undefined,
      sizeBytes: 18200,
      source: 'upload',
      createdAt: '2025-03-05T07:05:00Z',
    },
  ],
  default: [],
};

const getFallbackCollection = <T>(collection: Record<string, T[]>, patientId?: string | number) => {
  if (!patientId) return collection.default;
  const key = patientId.toString().toLowerCase();
  return collection[patientId.toString()] ?? collection[key] ?? collection.default;
};

const fallbackAuditLog: AuditLogEntry[] = [
  {
    id: 'audit-1',
    actor: 'director@lkcmedical.com',
    role: 'medical_director',
    action: 'auth.login',
    resource: 'auth',
    detail: 'Session started via dashboard preview.',
    timestamp: new Date(Date.now() - 1000 * 60 * 5).toISOString(),
  },
  {
    id: 'audit-2',
    actor: 'surgery@lkcmedical.com',
    role: 'surgery',
    action: 'patient.note.create',
    resource: 'patient:luna',
    detail: 'Rounds update added from OR tablet.',
    timestamp: new Date(Date.now() - 1000 * 60 * 25).toISOString(),
  },
  {
    id: 'audit-3',
    actor: 'billing@lkcmedical.com',
    role: 'billing',
    action: 'billing.invoice.review',
    resource: 'invoice:INV-9201',
    detail: 'Payment plan approved.',
    timestamp: new Date(Date.now() - 1000 * 60 * 50).toISOString(),
  },
];

const currencyToNumber = (raw: string | number | undefined) => {
  if (typeof raw === 'number') return raw;
  if (!raw) return 0;
  const cleaned = raw.replace(/[^\d.-]/g, '');
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : 0;
};

const normalizeStatus = (value: string | undefined) =>
  (value ?? 'Scheduled').replace(/\b\w/g, (char) => char.toUpperCase());

const deriveOpsSummary = (
  patients: Patient[],
  appointments: Appointment[],
  invoices: BillingWorkspacePayload['invoices'],
): OpsSummary => {
  const todayKey = new Date().toISOString().slice(0, 10);
  const todayAppointments = appointments.filter((appt) => {
    const date = new Date(appt.date);
    if (Number.isNaN(date.getTime())) {
      return false;
    }
    return date.toISOString().slice(0, 10) === todayKey;
  });

  const statusMap = new Map<string, number>();
  appointments.forEach((appt) => {
    const normalized = normalizeStatus(appt.status).toLowerCase();
    statusMap.set(normalized, (statusMap.get(normalized) ?? 0) + 1);
  });
  if (statusMap.size === 0) {
    ['check-in', 'pre-op', 'follow-up', 'completed'].forEach((status) =>
      statusMap.set(status, 0),
    );
  }

  let pendingRevenue = 0;
  let collectedRevenue = 0;
  invoices.forEach((invoice) => {
    const amount = currencyToNumber(
      typeof invoice === 'object' ? (invoice as any).amount ?? invoice.balance : 0,
    );
    const status = (invoice.status ?? 'pending').toLowerCase();
    if (status === 'paid' || status === 'collected') {
      collectedRevenue += amount;
    } else {
      pendingRevenue += amount;
    }
  });

  const appointmentStatus = Array.from(statusMap.entries()).map(([status, count]) => ({
    status: normalizeStatus(status),
    count,
  }));

  return {
    metrics: [
      {
        id: 'patients',
        label: 'Active Patients',
        value: patients.length,
        trend: '+12 this week',
      },
      {
        id: 'appointments',
        label: "Today? Appointments",
        value: todayAppointments.length || appointments.length,
        trend: `${statusMap.get('pre-op') ?? 0} awaiting prep`,
      },
      {
        id: 'surgery',
        label: 'Surgery Queue',
        value: statusMap.get('pre-op') ?? 0,
        trend: 'Operating rooms staffed',
      },
      {
        id: 'revenue',
        label: 'Pending Revenue',
        value: pendingRevenue,
        trend: `$${Math.round(collectedRevenue).toLocaleString()} collected`,
      },
    ],
    appointmentStatus,
    revenue: {
      pending: pendingRevenue,
      collected: collectedRevenue,
    },
  };
};

const fallbackOpsSummary = deriveOpsSummary(
  fallbackPatients,
  fallbackAppointments,
  fallbackBillingWorkspace.invoices,
);

// Query keys
export const patientsQueryKey = ['patients'] as const;
export const appointmentsQueryKey = ['appointments'] as const;
export const carePlansQueryKey = ['care-plans'] as const;
export const billingWorkspaceQueryKey = ['billing-workspace'] as const;
export const opsSummaryQueryKey = ['ops-summary'] as const;
export const auditLogQueryKey = ['audit-log'] as const;
const patientNotesQueryKey = (patientId: string | number) => ['patient-notes', patientId] as const;
const patientPrescriptionsQueryKey = (patientId: string | number) => ['patient-prescriptions', patientId] as const;
const patientLabResultsQueryKey = (patientId: string | number) => ['patient-labs', patientId] as const;
const patientAttachmentsQueryKey = (patientId: string | number) => ['patient-attachments', patientId] as const;

// Fetchers
export const login = async (credentials: { email: string; password: string }) => {
  const payload = await request('/api/auth/login', loginResponseSchema, {
    method: 'POST',
    body: JSON.stringify(credentials),
  });
  setAuthToken(payload.token);
  return payload;
};

export const fetchPatients = async () => {
  try {
    const data = await request('/api/patients', patientsSchema);
    return wrapValue(data);
  } catch (error) {
    console.warn('Falling back to demo patients', error);
    return wrapValue(
      fallbackPatients,
      true,
      'Real-time patient registry unavailable. Showing demo roster.',
    );
  }
};

export const fetchAppointments = async () => {
  try {
    const data = await request('/api/appointments', appointmentsSchema);
    return wrapValue(data);
  } catch (error) {
    console.warn('Falling back to demo appointments', error);
    return wrapValue(
      fallbackAppointments,
      true,
      'Live appointment feed unreachable. Showing cached schedule.',
    );
  }
};

export const fetchCarePlans = async () => {
  try {
    const data = await request('/api/care-plans', carePlanSchema);
    return wrapValue(data);
  } catch (error) {
    console.warn('Falling back to static care plans', error);
    return wrapValue(
      fallbackCarePlans,
      true,
      'Care coordination service unreachable. Showing standby plan.',
    );
  }
};

export const fetchBillingWorkspace = async () => {
  try {
    const data = await request('/api/billing', billingSchema);
    return wrapValue(data);
  } catch (error) {
    console.warn('Falling back to cached billing data', error);
    return wrapValue(
      fallbackBillingWorkspace,
      true,
      'Billing service offline. Displaying cached workspace.',
    );
  }
};

export const fetchOpsSummary = async () => {
  try {
    const data = await request('/api/ops/summary', opsSummarySchema);
    return wrapValue(data);
  } catch (error) {
    console.warn('Falling back to cached operations summary', error);
    return wrapValue(
      fallbackOpsSummary,
      true,
      'Operations summary offline. Displaying cached pulse.',
    );
  }
};

export const fetchAuditLog = async () => {
  try {
    const data = await request('/api/audit-log', auditLogSchema);
    return wrapValue(data);
  } catch (error) {
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
      throw error;
    }
    console.warn('Falling back to cached audit log', error);
    return wrapValue(
      fallbackAuditLog,
      true,
      'Audit service unavailable. Displaying recent cached activity.',
    );
  }
};

const fetchPatientNotes = async (patientId: string | number) => {
  try {
    const data = await request(`/api/patients/${patientId}/notes`, patientNotesSchema);
    return wrapValue(data);
  } catch (error) {
    console.warn('Falling back to cached patient notes', error);
    return wrapValue(
      getFallbackPatientNotes(patientId),
      true,
      'Notes service offline. Displaying cached timeline.',
    );
  }
};

const fetchPrescriptions = async (patientId: string | number) => {
  try {
    const data = await request(`/api/patients/${patientId}/prescriptions`, prescriptionsSchema);
    return wrapValue(data);
  } catch (error) {
    console.warn('Falling back to cached prescriptions', error);
    return wrapValue(
      getFallbackCollection(fallbackPrescriptions, patientId),
      true,
      'Prescription service offline. Displaying cached orders.',
    );
  }
};

const fetchLabResults = async (patientId: string | number) => {
  try {
    const data = await request(`/api/patients/${patientId}/labs`, labResultsSchema);
    return wrapValue(data);
  } catch (error) {
    console.warn('Falling back to cached lab results', error);
    return wrapValue(
      getFallbackCollection(fallbackLabResults, patientId),
      true,
      'Lab reporting offline. Displaying cached findings.',
    );
  }
};

const fetchAttachments = async (patientId: string | number) => {
  try {
    const data = await request(`/api/patients/${patientId}/attachments`, attachmentsSchema);
    return wrapValue(data);
  } catch (error) {
    console.warn('Falling back to cached attachments', error);
    return wrapValue(
      getFallbackCollection(fallbackAttachments, patientId),
      true,
      'Attachment vault unreachable. Displaying cached files.',
    );
  }
};

const persistPatientNote = async (
  patientId: string | number,
  payload: { note: string; category?: string; author?: string; role?: string },
) =>
  request(`/api/patients/${patientId}/notes`, patientNoteSchema, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

const persistPrescription = async (
  patientId: string | number,
  payload: {
    medication: string;
    dosage?: string;
    frequency?: string;
    duration?: string;
    instructions?: string;
    status?: string;
  },
) =>
  request(`/api/patients/${patientId}/prescriptions`, prescriptionSchema, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

const persistLabResult = async (
  patientId: string | number,
  payload: {
    testName: string;
    status?: string;
    findings?: string;
    performedAt?: string | null;
    attachmentId?: string | null;
  },
) =>
  request(`/api/patients/${patientId}/labs`, labResultSchema, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

const persistAttachment = async (
  patientId: string | number,
  payload: { filename: string; contentType?: string; contentBase64: string; source?: string },
) =>
  request(`/api/patients/${patientId}/attachments`, attachmentSchema, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

const advanceAppointmentStatus = async (
  appointmentId: string | number,
  payload: { status?: string; note?: string },
) =>
  request(`/api/appointments/${appointmentId}/advance`, appointmentActionSchema, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

const sendAppointmentReminder = async (
  appointmentId: string | number,
  payload: { channel?: string; message?: string },
) =>
  request(`/api/appointments/${appointmentId}/reminder`, reminderResponseSchema, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

const markInvoicePaid = async (invoiceId: string, payload?: { reference?: string }) =>
  request(`/api/billing/${invoiceId}/mark-paid`, billingActionSchema, {
    method: 'POST',
    body: JSON.stringify(payload ?? {}),
  });

const sendInvoiceReminder = async (invoiceId: string, payload?: { channel?: string; message?: string }) =>
  request(`/api/billing/${invoiceId}/remind`, reminderResponseSchema, {
    method: 'POST',
    body: JSON.stringify(payload ?? {}),
  });

// Hooks
export const usePatients = () => {
  const query = useQuery({
    queryKey: patientsQueryKey,
    queryFn: fetchPatients,
    placeholderData: () => wrapValue(fallbackPatients, true),
  });
  return toHookResult(query, fallbackPatients);
};

export const useAppointments = () => {
  const query = useQuery({
    queryKey: appointmentsQueryKey,
    queryFn: fetchAppointments,
    placeholderData: () => wrapValue(fallbackAppointments, true),
  });
  return toHookResult(query, fallbackAppointments);
};

export const useCarePlans = () => {
  const query = useQuery({
    queryKey: carePlansQueryKey,
    queryFn: fetchCarePlans,
    placeholderData: () => wrapValue(fallbackCarePlans, true),
  });
  return toHookResult(query, fallbackCarePlans);
};

export const useBillingWorkspace = () => {
  const query = useQuery({
    queryKey: billingWorkspaceQueryKey,
    queryFn: fetchBillingWorkspace,
    placeholderData: () => wrapValue(fallbackBillingWorkspace, true),
  });
  return toHookResult(query, fallbackBillingWorkspace);
};

export const useOpsSummary = () => {
  const query = useQuery({
    queryKey: opsSummaryQueryKey,
    queryFn: fetchOpsSummary,
    placeholderData: () => wrapValue(fallbackOpsSummary, true),
  });
  return toHookResult(query, fallbackOpsSummary);
};

export const useAuditLog = (enabled = true) => {
  const query = useQuery({
    queryKey: auditLogQueryKey,
    queryFn: fetchAuditLog,
    enabled,
    placeholderData: () => wrapValue(fallbackAuditLog, true),
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });
  return toHookResult(query, fallbackAuditLog);
};

export const usePatientNotes = (patientId?: string | number) => {
  const enabled = Boolean(patientId);
  const fallbackNotes = patientId ? getFallbackPatientNotes(patientId) : [];
  const query = useQuery({
    queryKey: patientId ? patientNotesQueryKey(patientId) : ['patient-notes', 'noop'],
    queryFn: () => fetchPatientNotes(patientId!),
    enabled,
    placeholderData: () => wrapValue(fallbackNotes, true),
  });

  if (!enabled) {
    return {
      ...query,
      data: [] as PatientNote[],
      isFallback: false,
      fallbackMessage: undefined as string | undefined,
    };
  }

  return toHookResult(query as UseQueryResult<QueryEnvelope<PatientNote[]>>, fallbackNotes);
};

export const usePatientPrescriptions = (patientId?: string | number) => {
  const enabled = Boolean(patientId);
  const fallbackValue = patientId ? getFallbackCollection(fallbackPrescriptions, patientId) : [];
  const query = useQuery({
    queryKey: patientId ? patientPrescriptionsQueryKey(patientId) : ['patient-prescriptions', 'noop'],
    queryFn: () => fetchPrescriptions(patientId!),
    enabled,
    placeholderData: () => wrapValue(fallbackValue, true),
  });
  if (!enabled) {
    return {
      ...query,
      data: [] as Prescription[],
      isFallback: false,
      fallbackMessage: undefined as string | undefined,
    };
  }
  return toHookResult(query as UseQueryResult<QueryEnvelope<Prescription[]>>, fallbackValue);
};

export const usePatientLabResults = (patientId?: string | number) => {
  const enabled = Boolean(patientId);
  const fallbackValue = patientId ? getFallbackCollection(fallbackLabResults, patientId) : [];
  const query = useQuery({
    queryKey: patientId ? patientLabResultsQueryKey(patientId) : ['patient-labs', 'noop'],
    queryFn: () => fetchLabResults(patientId!),
    enabled,
    placeholderData: () => wrapValue(fallbackValue, true),
  });
  if (!enabled) {
    return {
      ...query,
      data: [] as LabResult[],
      isFallback: false,
      fallbackMessage: undefined as string | undefined,
    };
  }
  return toHookResult(query as UseQueryResult<QueryEnvelope<LabResult[]>>, fallbackValue);
};

export const usePatientAttachments = (patientId?: string | number) => {
  const enabled = Boolean(patientId);
  const fallbackValue = patientId ? getFallbackCollection(fallbackAttachments, patientId) : [];
  const query = useQuery({
    queryKey: patientId ? patientAttachmentsQueryKey(patientId) : ['patient-attachments', 'noop'],
    queryFn: () => fetchAttachments(patientId!),
    enabled,
    placeholderData: () => wrapValue(fallbackValue, true),
  });
  if (!enabled) {
    return {
      ...query,
      data: [] as PatientAttachment[],
      isFallback: false,
      fallbackMessage: undefined as string | undefined,
    };
  }
  return toHookResult(query as UseQueryResult<QueryEnvelope<PatientAttachment[]>>, fallbackValue);
};

// Mutations
const buildLocalPatient = (payload: PatientFormData): Patient => ({
  id:
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : Date.now().toString(),
  name: payload.name.trim(),
  age: Number(payload.age),
  species: payload.species.trim().toLowerCase(),
  owner: payload.ownerName.trim(),
  doctor: 'Dr. Avery',
  lastVisit: new Date().toLocaleDateString(),
});

const persistPatient = async (payload: PatientFormData): Promise<Patient> => {
  try {
    return await request('/api/patients', patientSchema, {
      method: 'POST',
      body: JSON.stringify({
        name: payload.name,
        age: Number(payload.age),
        species: payload.species,
        owner: payload.ownerName,
        contact: payload.contact,
      }),
    });
  } catch (error) {
    console.warn('Unable to reach API, storing patient locally', error);
    return buildLocalPatient(payload);
  }
};

export const useCreatePatient = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: persistPatient,
    onSuccess: (newPatient) => {
      queryClient.setQueryData<QueryEnvelope<Patient[]>>(patientsQueryKey, (existing) => {
        const current = existing?.value ?? [];
        return {
          value: [newPatient, ...current],
          fallback: existing?.fallback ?? false,
          message: existing?.message,
        };
      });
    },
  });
};

export const useCreatePrescription = (patientId?: string | number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof persistPrescription>[1]) => {
      if (!patientId) throw new Error('Patient ID is required');
      return persistPrescription(patientId, payload);
    },
    onSuccess: (record) => {
      if (!patientId) return;
      queryClient.setQueryData<QueryEnvelope<Prescription[]>>(
        patientPrescriptionsQueryKey(patientId),
        (existing) => {
          const current = existing?.value ?? [];
          return {
            value: [record, ...current],
            fallback: existing?.fallback ?? false,
            message: existing?.message,
          };
        },
      );
    },
  });
};

export const useCreateLabResult = (patientId?: string | number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof persistLabResult>[1]) => {
      if (!patientId) throw new Error('Patient ID is required');
      return persistLabResult(patientId, payload);
    },
    onSuccess: (record) => {
      if (!patientId) return;
      queryClient.setQueryData<QueryEnvelope<LabResult[]>>(
        patientLabResultsQueryKey(patientId),
        (existing) => {
          const current = existing?.value ?? [];
          return {
            value: [record, ...current],
            fallback: existing?.fallback ?? false,
            message: existing?.message,
          };
        },
      );
    },
  });
};

export const useUploadAttachment = (patientId?: string | number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Parameters<typeof persistAttachment>[1]) => {
      if (!patientId) throw new Error('Patient ID is required');
      return persistAttachment(patientId, payload);
    },
    onSuccess: (record) => {
      if (!patientId) return;
      queryClient.setQueryData<QueryEnvelope<PatientAttachment[]>>(
        patientAttachmentsQueryKey(patientId),
        (existing) => {
          const current = existing?.value ?? [];
          return {
            value: [record, ...current],
            fallback: existing?.fallback ?? false,
            message: existing?.message,
          };
        },
      );
    },
  });
};

export const useAddPatientNote = (patientId?: string | number) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { note: string; category?: string; author?: string; role?: string }) => {
      if (!patientId) {
        throw new Error('Patient ID is required');
      }
      return persistPatientNote(patientId, payload);
    },
    onSuccess: (note) => {
      if (!patientId) return;
      queryClient.setQueryData<QueryEnvelope<PatientNote[]>>(patientNotesQueryKey(patientId), (existing) => {
        const current = existing?.value ?? [];
        return {
          value: [note, ...current],
          fallback: existing?.fallback ?? false,
          message: existing?.message,
        };
      });
    },
  });
};

export const useAdvanceAppointment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { appointmentId: string | number; status?: string; note?: string }) =>
      advanceAppointmentStatus(payload.appointmentId, { status: payload.status, note: payload.note }),
    onSuccess: (result, variables) => {
      queryClient.setQueryData<QueryEnvelope<Appointment[]>>(appointmentsQueryKey, (existing) => {
        if (!existing) return existing;
        const next = existing.value.map((appt) =>
          String(appt.id) === String(variables.appointmentId)
            ? { ...appt, status: result.status ?? appt.status }
            : appt,
        );
        return { ...existing, value: next };
      });
    },
  });
};

export const useAppointmentReminder = () =>
  useMutation({
    mutationFn: (payload: { appointmentId: string | number; channel?: string; message?: string }) =>
      sendAppointmentReminder(payload.appointmentId, { channel: payload.channel, message: payload.message }),
  });

export const useMarkInvoicePaid = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { invoiceId: string; reference?: string }) =>
      markInvoicePaid(payload.invoiceId, payload.reference ? { reference: payload.reference } : undefined),
    onSuccess: (result, variables) => {
      queryClient.setQueryData<QueryEnvelope<BillingWorkspacePayload>>(billingWorkspaceQueryKey, (existing) => {
        if (!existing) return existing;
        const next = {
          ...existing.value,
          invoices: existing.value.invoices.map((invoice) =>
            invoice.id === variables.invoiceId
              ? { ...invoice, status: result.status ?? 'Paid', balance: result.balance ?? '$0' }
              : invoice,
          ),
        };
        return { ...existing, value: next };
      });
    },
  });
};

export const useInvoiceReminder = () =>
  useMutation({
    mutationFn: (payload: { invoiceId: string; channel?: string; message?: string }) =>
      sendInvoiceReminder(payload.invoiceId, { channel: payload.channel, message: payload.message }),
  });

export const __private__ = {
  fallbackPatients,
  fallbackAppointments,
  fallbackCarePlans,
  fallbackBillingWorkspace,
  fallbackOpsSummary,
  getFallbackPatientNotes,
  fallbackPrescriptions,
  fallbackLabResults,
  fallbackAttachments,
  fallbackAuditLog,
  wrapValue,
};


